import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import { DashboardNarrativePanel } from "src/components/common";
import { useLanguage } from "src/hooks/useLanguage";
import { useMockAiSummaryUsage } from "src/hooks/useMockAiSummaryUsage";
import { toArabicIndicDigitsInString } from "src/utils/numberFormat";
import {
  generateOmnichannelExecutiveSummary,
  type OmnichannelSummaryTab,
} from "./OmnichannelExecutiveSummary.data";
import type {
  OmnichannelCustomer,
  OmnichannelOrder,
  OmnichannelProduct,
} from "./OmnichannelAnalyticsTabsDashboard.data";

interface OmnichannelKpis {
  totalRevenue: number;
  totalOrders: number;
  activeCustomers: number;
  avgOrderValue: number;
  inventoryValue: number;
  avgCltv: number;
}

interface OmnichannelDistributionPoint {
  label: string;
  value: number;
  percentage?: number;
}

interface OmnichannelFinancialSummary {
  revenue: number;
  cogs: number;
  grossProfit: number;
  taxes: number;
  expenses: number;
  netProfit: number;
  refunds: number;
}

interface OmnichannelMonthlyPoint {
  month: string;
  revenue: number;
  expenses: number;
  profit: number;
}

interface OmnichannelExecutiveSummaryProps {
  tab: OmnichannelSummaryTab;
  dateRange: { startDate: string; endDate: string };
  kpis?: OmnichannelKpis;
  previousKpis?: OmnichannelKpis;
  channelData?: OmnichannelDistributionPoint[];
  segmentation?: OmnichannelDistributionPoint[];
  products?: OmnichannelProduct[];
  orders?: OmnichannelOrder[];
  customers?: OmnichannelCustomer[];
  summary?: OmnichannelFinancialSummary;
  previousSummary?: OmnichannelFinancialSummary;
  monthly?: OmnichannelMonthlyPoint[];
  taxBreakdown?: OmnichannelDistributionPoint[];
}

const AI_SUMMARY_STORAGE_KEY = "omnichannel_analytics_ai_summary_enabled";

const readStoredSummaryEnabled = (): boolean => {
  try {
    return localStorage.getItem(AI_SUMMARY_STORAGE_KEY) !== "false";
  } catch {
    return true;
  }
};

const writeStoredSummaryEnabled = (enabled: boolean) => {
  try {
    localStorage.setItem(AI_SUMMARY_STORAGE_KEY, String(enabled));
  } catch {
    // Storage can be unavailable (private mode, blocked site data) — the toggle
    // simply won't persist across sessions in that case.
  }
};

const localizeDigits = (text: string, isRTL: boolean) =>
  isRTL ? toArabicIndicDigitsInString(text) : text;

const OmnichannelExecutiveSummary: FC<OmnichannelExecutiveSummaryProps> = ({
  tab,
  dateRange,
  kpis,
  previousKpis,
  channelData,
  segmentation,
  products,
  orders,
  customers,
  summary,
  previousSummary,
  monthly,
  taxBreakdown,
}) => {
  const { isRTL } = useLanguage();
  const [analyzing, setAnalyzing] = useState(false);
  const [summaryEnabled, setSummaryEnabled] = useState(readStoredSummaryEnabled);

  const handleSummaryEnabledChange = useCallback((enabled: boolean) => {
    setSummaryEnabled(enabled);
    writeStoredSummaryEnabled(enabled);
  }, []);

  const filterKey = `${dateRange.startDate}|${dateRange.endDate}|${tab}`;

  useEffect(() => {
    if (!summaryEnabled) {
      setAnalyzing(false);
      return;
    }
    setAnalyzing(true);
    const id = window.setTimeout(() => setAnalyzing(false), 420);
    return () => window.clearTimeout(id);
  }, [filterKey, summaryEnabled]);

  const fmt = useMemo(
    () => ({
      formatCurrency: (value: number) => localizeDigits(`SAR ${Math.round(value).toLocaleString()}`, isRTL),
      formatCompactCurrency: (value: number) =>
        localizeDigits(value >= 1000 ? `SAR ${(value / 1000).toFixed(1)}k` : `SAR ${Math.round(value)}`, isRTL),
      formatNumber: (value: number) => localizeDigits(String(value), isRTL),
      isRTL,
    }),
    [isRTL],
  );

  const summaryUsage = useMockAiSummaryUsage("omnichannel_analytics", filterKey, summaryEnabled);

  const summaryContent = generateOmnichannelExecutiveSummary({
    tab,
    fmt,
    dateRange,
    kpis,
    previousKpis,
    channelData,
    segmentation,
    products,
    orders,
    customers,
    summary,
    previousSummary,
    monthly,
    taxBreakdown,
  });

  return (
    <DashboardNarrativePanel
      narrative={summaryContent.hasData ? summaryContent : null}
      loading={analyzing}
      defaultCollapsed
      title={isRTL ? "الملخص التنفيذي بالذكاء الاصطناعي" : "AI Executive Summary"}
      subtitle={
        summaryContent.periodLabel
          ? isRTL
            ? `مدعوم بواسطة DataTram AI · ${summaryContent.periodLabel}`
            : `Powered by DataTram AI · ${summaryContent.periodLabel}`
          : undefined
      }
      aiSummary={{
        enabled: summaryEnabled,
        featureAvailable: true,
        setEnabled: handleSummaryEnabledChange,
        usage: summaryUsage,
      }}
    />
  );
};

export default OmnichannelExecutiveSummary;
